// Overview tab of a warehouse or a standalone location (owner, 2026-09-20): the same summary card a project's
// Overview shows, with the place's own facts. It replaces PlaceOverview, whose tally bar, "Gateways online" tile
// and loose stock-count list were three unrelated denominators floating under the tabs — every figure is now a
// labelled row, and the counts link into the tab that holds their detail (Assets, Stock counts, Zones).
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import RouterOutlinedIcon from '@mui/icons-material/RouterOutlined';
import WarehouseOutlinedIcon from '@mui/icons-material/WarehouseOutlined';
import { Alert, Box, Link, Stack, Typography } from '@mui/material';
import type { CountStatus } from '@fleetspoke/core/counts';
import type { PlaceKind } from '@fleetspoke/core/places';
import { tally } from '@fleetspoke/core/sites';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../auth/session';
import type { SiteOverview } from '../../data/queries';
import { resultText } from '../../lib/counts';
import { calendarDate, dateTime } from '../../lib/time';
import { CountStateChip } from '../../pages/CountsPage';
import { placeTypeLabel } from './SiteCard';
import { SummaryCard, SummaryCount, SummaryRow } from './SummaryCard';

export function PlaceSummary({
  site,
  kind,
  count,
  zones,
  onOpenAssets,
  onOpenCounts,
  onOpenZones,
}: {
  site: SiteOverview;
  kind: PlaceKind;
  count?: CountStatus;
  /** active zones (CR_20260914); the row opens the Zones dialog for managers */
  zones: number;
  onOpenAssets?: () => void;
  onOpenCounts?: () => void;
  onOpenZones?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { me } = useSession();
  const tz = me?.company?.timezone ?? 'Asia/Kuala_Lumpur';
  const lang = i18n.language;
  const figures = tally([site]);
  const offline = site.gateways > site.gateways_online;

  const countLine = [
    count?.last_count
      ? t('counts.last_line', {
          when: dateTime(count.last_count.closed_at, lang, tz),
          result: resultText(count.last_count, t).label,
        })
      : t('counts.never'),
    count?.next_due && count.state !== 'upcoming'
      ? t('counts.board_next', { date: calendarDate(count.next_due, lang) })
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Box data-testid="place-overview">
      {site.is_active ? null : (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('sites.inactive_notice')}
        </Alert>
      )}
      {offline ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('board.gateway_offline')}
        </Alert>
      ) : null}
      <SummaryCard
        title={t(kind === 'warehouse' ? 'places.summary_warehouse' : 'places.summary_site')}
        testId="place-summary"
      >
        <SummaryRow icon={<WarehouseOutlinedIcon fontSize="small" />} label={t('places.row_type')}>
          <Typography variant="body2">{placeTypeLabel(site.type, t)}</Typography>
        </SummaryRow>
        <SummaryRow
          icon={<Inventory2OutlinedIcon fontSize="small" />}
          label={t('projects.total_assets')}
        >
          <SummaryCount n={figures.expected} onOpen={onOpenAssets} />
        </SummaryRow>
        <SummaryRow icon={<CheckCircleOutlineIcon fontSize="small" />} label={t('sites.on_site')}>
          <Typography variant="body2">
            {t('places.count_of', { on: figures.on_site, total: figures.expected })}
          </Typography>
        </SummaryRow>
        <SummaryRow icon={<GridViewOutlinedIcon fontSize="small" />} label={t('zones.button')}>
          <SummaryCount n={zones} onOpen={onOpenZones} />
        </SummaryRow>
        <SummaryRow icon={<RouterOutlinedIcon fontSize="small" />} label={t('places.fig_gateways')}>
          <Typography variant="body2" color={offline ? 'error.main' : undefined}>
            {site.gateways ? `${site.gateways_online} / ${site.gateways}` : '—'}
          </Typography>
        </SummaryRow>
        <SummaryRow icon={<FactCheckOutlinedIcon fontSize="small" />} label={t('counts.title')}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            data-testid="board-count"
          >
            {count ? (
              <CountStateChip s={count} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('counts.state_not_scheduled')}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary">
              {count ? countLine : t('counts.never')}
            </Typography>
            {onOpenCounts ? (
              <Link component="button" type="button" variant="body2" onClick={onOpenCounts}>
                {t('places.tab_counts')} →
              </Link>
            ) : null}
          </Stack>
        </SummaryRow>
      </SummaryCard>
    </Box>
  );
}
